import React, { useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Grid,
    Paper
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57'];

const UserAnalytics = ({ open, handleClose, users }) => {
    // Only calculate these when users or open changes
    const analytics = useMemo(() => {
        if (!users || users.length === 0) return null;

        // Course distribution
        const courseData = {};
        users.forEach(user => {
            if (user.course) {
                courseData[user.course] = (courseData[user.course] || 0) + 1;
            }
        });

        const courseChartData = Object.entries(courseData).map(([name, value]) => ({
            name,
            value
        }));

        // CGPA distribution
        const cgpaRanges = {
            '<6': 0,
            '6-7': 0,
            '7-8': 0,
            '8-9': 0,
            '9-10': 0
        };

        users.forEach(user => {
            const cgpa = parseFloat(user.cgpa);
            if (!isNaN(cgpa)) {
                if (cgpa < 6) cgpaRanges['<6']++;
                else if (cgpa < 7) cgpaRanges['6-7']++;
                else if (cgpa < 8) cgpaRanges['7-8']++;
                else if (cgpa < 9) cgpaRanges['8-9']++;
                else cgpaRanges['9-10']++;
            }
        });

        const cgpaChartData = Object.entries(cgpaRanges).map(([name, value]) => ({
            name,
            value
        }));

        // Year of passing distribution
        const yopData = {};
        users.forEach(user => {
            if (user.yearOfPassing) {
                yopData[user.yearOfPassing] = (yopData[user.yearOfPassing] || 0) + 1;
            }
        });

        const yopChartData = Object.entries(yopData)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => parseInt(a.name) - parseInt(b.name));

        // Active backlogs distribution
        const backlogsData = {
            '0': 0,
            '1': 0,
            '2': 0,
            '3+': 0
        };

        users.forEach(user => {
            const backlogs = parseInt(user.activeBacklogs);
            if (!isNaN(backlogs)) {
                if (backlogs === 0) backlogsData['0']++;
                else if (backlogs === 1) backlogsData['1']++;
                else if (backlogs === 2) backlogsData['2']++;
                else backlogsData['3+']++;
            }
        });

        const backlogsChartData = Object.entries(backlogsData).map(([name, value]) => ({
            name,
            value
        }));

        // Certificates distribution
        const certificatesData = {
            'No Certificates': 0,
            '1 Certificate': 0,
            '2 Certificates': 0,
            '3+ Certificates': 0
        };

        users.forEach(user => {
            if (!user.certificates || user.certificates.length === 0) {
                certificatesData['No Certificates']++;
            } else if (user.certificates.length === 1) {
                certificatesData['1 Certificate']++;
            } else if (user.certificates.length === 2) {
                certificatesData['2 Certificates']++;
            } else {
                certificatesData['3+ Certificates']++;
            }
        });

        const certificatesChartData = Object.entries(certificatesData).map(([name, value]) => ({
            name,
            value
        }));

        // 12th percentage distribution
        const twelfthData = {
            '<60%': 0,
            '60-70%': 0,
            '70-80%': 0,
            '80-90%': 0,
            '90-100%': 0
        };

        users.forEach(user => {
            const percentage = parseFloat(user.twelfthPercentage);
            if (!isNaN(percentage)) {
                if (percentage < 60) twelfthData['<60%']++;
                else if (percentage < 70) twelfthData['60-70%']++;
                else if (percentage < 80) twelfthData['70-80%']++;
                else if (percentage < 90) twelfthData['80-90%']++;
                else twelfthData['90-100%']++;
            }
        });

        const twelfthChartData = Object.entries(twelfthData).map(([name, value]) => ({
            name,
            value
        }));

        // Resume availability
        const resumeData = [
            { name: 'Has Resume', value: 0 },
            { name: 'No Resume', value: 0 }
        ];

        users.forEach(user => {
            if (user.resumeURL && user.resumeURL !== '---') {
                resumeData[0].value++;
            } else {
                resumeData[1].value++;
            }
        });

        // LinkedIn profile availability
        const linkedinData = [
            { name: 'Has LinkedIn', value: 0 },
            { name: 'No LinkedIn', value: 0 }
        ];

        users.forEach(user => {
            if (user.linkedin && user.linkedin !== '---' && user.linkedin !== '') {
                linkedinData[0].value++;
            } else {
                linkedinData[1].value++;
            }
        });

        // GitHub profile availability
        const githubData = [
            { name: 'Has GitHub', value: 0 },
            { name: 'No GitHub', value: 0 }
        ];

        users.forEach(user => {
            if (user.github && user.github !== '---' && user.github !== '') {
                githubData[0].value++;
            } else {
                githubData[1].value++;
            }
        });

        // LeetCode profile availability
        const leetCodeData = [
            { name: 'Has LeetCode', value: 0 },
            { name: 'No LeetCode', value: 0 }
        ];

        users.forEach(user => {
            if (user.leetCode && user.leetCode !== '---' && user.leetCode !== '') {
                leetCodeData[0].value++;
            } else {
                leetCodeData[1].value++;
            }
        });

        // Social profiles completeness
        const socialCompleteness = [
            { name: 'No Profiles', value: 0 },
            { name: 'One Profile', value: 0 },
            { name: 'Two Profiles', value: 0 },
            { name: 'All Profiles', value: 0 }
        ];

        users.forEach(user => {
            let count = 0;
            if (user.linkedin && user.linkedin !== '---' && user.linkedin !== '') count++;
            if (user.github && user.github !== '---' && user.github !== '') count++;
            if (user.leetCode && user.leetCode !== '---' && user.leetCode !== '') count++;

            socialCompleteness[count].value++;
        });

        return {
            courseChartData,
            cgpaChartData,
            yopChartData,
            backlogsChartData,
            certificatesChartData,
            twelfthChartData,
            resumeData,
            linkedinData,
            githubData,
            leetCodeData,
            socialCompleteness,
            totalStudents: users.length
        };
    }, [users, open]);

    if (!analytics) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                    Student Analytics
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    Total Students: {analytics.totalStudents}
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={3}>
                    {/* Course Distribution */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Course Distribution
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={analytics.courseChartData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        height={70}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#8884d8" name="Students" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* CGPA Distribution */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                CGPA Distribution
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={analytics.cgpaChartData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#82ca9d" name="Students" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Year of Passing */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Graduation Year
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart
                                    data={analytics.yopChartData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        stroke="#8884d8"
                                        name="Students"
                                        activeDot={{ r: 8 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Active Backlogs */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Active Backlogs
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={analytics.backlogsChartData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {analytics.backlogsChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => [`${value} Students`, 'Count']} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Certificates */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Certificates
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={analytics.certificatesChartData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {analytics.certificatesChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => [`${value} Students`, 'Count']} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* 12th Percentage */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                12th Percentage
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={analytics.twelfthChartData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#ffc658" name="Students" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Resume Availability */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Resume Availability
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={analytics.resumeData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        <Cell fill="#00C49F" />
                                        <Cell fill="#FF8042" />
                                    </Pie>
                                    <Tooltip formatter={(value) => [`${value} Students`, 'Count']} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Social Profiles Overview */}
                    <Grid item xs={12}>
                        <Typography variant="h6" component="div" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Social Profiles Analytics
                        </Typography>
                    </Grid>

                    {/* LinkedIn Profile Availability */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                LinkedIn Profile
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={analytics.linkedinData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        <Cell fill="#0077B5" /> {/* LinkedIn blue */}
                                        <Cell fill="#E0E0E0" />
                                    </Pie>
                                    <Tooltip formatter={(value) => [`${value} Students`, 'Count']} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* GitHub Profile Availability */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                GitHub Profile
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={analytics.githubData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        <Cell fill="#171515" /> {/* GitHub dark */}
                                        <Cell fill="#E0E0E0" />
                                    </Pie>
                                    <Tooltip formatter={(value) => [`${value} Students`, 'Count']} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* LeetCode Profile Availability */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                LeetCode Profile
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={analytics.leetCodeData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        <Cell fill="#FFA116" /> {/* LeetCode orange */}
                                        <Cell fill="#E0E0E0" />
                                    </Pie>
                                    <Tooltip formatter={(value) => [`${value} Students`, 'Count']} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Social Profiles Completeness */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Social Profile Completeness
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={analytics.socialCompleteness}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="value" fill="#8884d8" name="Students" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} color="primary" variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserAnalytics;